import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, Space, message, Spin, Switch, InputNumber, DatePicker, Row, Col, Divider, Select } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useLoaderData } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import PageHeader from '@shared/PageHeader';

import { orderPostProcessorService } from '@services/admin/orderPostProcessorService';
import SalesChannelSelect from '@components/shared/selects/SalesChannelSelect';

const { RangePicker } = DatePicker;
const { TextArea } = Input;

export default function OrderPostProcessorsEdit() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const initialValues = useLoaderData(); // Loaded by router loader

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  // 區分「儲存並新增下一筆」的 loading，避免兩顆按鈕同時轉 spinner
  const [savingAndAdding, setSavingAndAdding] = useState(false);
  const isEdit = !!id;
  const isNew = !isEdit;

  // 新建模式預設值抽成 callback，以便 resetFields 後重新套用
  const applyNewModeDefaults = () => {
    form.setFieldsValue({
      active: true,
      priority: 0,
      order_post_processor_rules_attributes: [],
      sales_channel_ids: [],
    });
  };

  // 組合送出 payload（兩個提交按鈕共用）
  const buildPayload = (values) => {
    const payload = {
      ...values,
      // Handle RangePicker
      start_datetime: values.validity ? values.validity[0].toISOString() : null,
      end_datetime: values.validity ? values.validity[1].toISOString() : null,
    };
    delete payload.validity;
    return payload;
  };

  // 核心提交邏輯：呼叫 create/update 並回傳 response 給呼叫者決定後續行為
  const submitValues = async (values) => {
    const payload = buildPayload(values);
    return isEdit
      ? await orderPostProcessorService.update(id, payload)
      : await orderPostProcessorService.create(payload);
  };

  // 主要提交：成功後導向 show 頁
  const onFinish = async (values) => {
    setLoading(true);
    try {
      const response = await submitValues(values);

      if (response?.success !== false) {
        message.success(
          isEdit
            ? t('common.update_success', 'Updated successfully')
            : t('common.create_success', 'Created successfully')
        );
        // 導向該筆記錄的 show 頁，避免返回快取的列表頁看不到新資料
        const recordId = response?.data?.id || id;
        navigate(`/order_post_processors/${recordId}`, { replace: true });
      } else {
        message.error(response?.message || t('common.error.operation_failed', 'Operation failed'));
      }
    } catch (error) {
      console.error(error);
      message.error(t('common.error.operation_failed', 'Operation failed'));
    } finally {
      setLoading(false);
    }
  };

  // 儲存並新增下一筆：僅在新建模式可用，成功後重置表單留在本頁
  const handleSaveAndAddAnother = async () => {
    try {
      const values = await form.validateFields();
      setSavingAndAdding(true);
      try {
        const response = await submitValues(values);

        if (response?.success !== false) {
          message.success(t('common.create_success', 'Created successfully'));
          // 重置 antd form + 重新套用新建模式預設值
          form.resetFields();
          applyNewModeDefaults();
        } else {
          message.error(response?.message || t('common.error.operation_failed', 'Operation failed'));
        }
      } catch (error) {
        console.error(error);
        message.error(t('common.error.operation_failed', 'Operation failed'));
      } finally {
        setSavingAndAdding(false);
      }
    } catch {
      // validateFields 失敗會 reject，antd 會自動高亮錯誤欄位
    }
  };

  // Initial values handling
  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue({
        ...initialValues,
        validity: (initialValues.start_datetime || initialValues.end_datetime)
          ? [
            initialValues.start_datetime ? dayjs(initialValues.start_datetime) : null,
            initialValues.end_datetime ? dayjs(initialValues.end_datetime) : null
          ]
          : [],
        sales_channel_ids: initialValues.sales_channel_ids || []
      });
    } else {
      applyNewModeDefaults();
    }
  }, [initialValues, form]);

  return (
    <div>
      <PageHeader
        basePath="/admin"
        items={['orders', 'orders.post_processors', { label: isEdit ? t('admin.pages.order_post_processors.edit.title', 'Edit Rule') : t('admin.pages.order_post_processors.new.title', 'New Rule') }]}
      />

      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
        >
          <Card title={t('common.basic_info', 'Basic Info')} className="mb-4">
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="name"
                  label={t('admin.pages.order_post_processors.fields.name', 'Rule Name')}
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="active"
                  label={t('admin.pages.order_post_processors.fields.active', 'Status')}
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="priority"
                  label={t('admin.pages.order_post_processors.fields.priority', 'Priority')}
                  help={t('admin.pages.order_post_processors.fields.priority_help', 'Higher number runs first')}
                >
                  <InputNumber style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="validity"
                  label={t('admin.pages.order_post_processors.fields.validity', 'Validity Period')}
                >
                  <RangePicker
                    showTime
                    style={{ width: '100%' }}
                    placeholder={[
                      t('common.table.filters.date_range_start_placeholder'),
                      t('common.table.filters.date_range_end_placeholder'),
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="sales_channel_ids"
                  label={t('models.sales_channel.name', 'Merchant Channels')}
                  help={t('admin.pages.order_post_processors.fields.sales_channels_help', 'Leave empty to apply to all channels')}
                >
                  <SalesChannelSelect mode="multiple" />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="description"
                  label={t('admin.pages.order_post_processors.fields.description', 'Description')}
                >
                  <TextArea rows={3} />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          <Card title={t('admin.pages.order_post_processors.sections.rules', 'Rules & Actions')} className="mb-4">
            <Form.List name="order_post_processor_rules_attributes">
              {(fields, { add, remove }) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {fields.map((field) => (
                    <Card
                      key={field.key}
                      type="inner"
                      title={`${t('admin.pages.order_post_processors.fields.rule', 'Rule')} #${field.name + 1}`}
                      extra={<MinusCircleOutlined onClick={() => remove(field.name)} />}
                      bodyStyle={{ backgroundColor: '#fafafa' }}
                    >
                      {/* Hidden ID for update */}
                      <Form.Item name={[field.name, 'id']} hidden><Input /></Form.Item>
                      <Form.Item name={[field.name, '_destroy']} hidden><Input /></Form.Item>

                      <Row gutter={16}>
                        <Col span={8}>
                          <Form.Item
                            name={[field.name, 'condition_type']}
                            label={t('admin.pages.order_post_processors.fields.condition_type', 'Condition')}
                            rules={[{ required: true }]}
                          >
                            <Select options={[
                              { label: t('common.order_rules.condition.total_amount', 'Order Amount'), value: 'total_amount' },
                              { label: t('common.order_rules.condition.total_weight', 'Order Weight'), value: 'total_weight' },
                              { label: t('common.order_rules.condition.item_count', 'Item Count'), value: 'item_count' },
                              { label: t('common.order_rules.condition.contains_product', 'Contains Product'), value: 'contains_product' },
                            ]} />
                          </Form.Item>
                        </Col>
                        <Col span={6}>
                          <Form.Item
                            name={[field.name, 'operator']}
                            label={t('admin.pages.order_post_processors.fields.operator', 'Operator')}
                            rules={[{ required: true }]}
                          >
                            <Select options={[
                              { label: '>', value: 'gt' },
                              { label: '>=', value: 'gte' },
                              { label: '<', value: 'lt' },
                              { label: '<=', value: 'lte' },
                              { label: '=', value: 'eq' },
                              { label: '≠', value: 'neq' },
                            ]} />
                          </Form.Item>
                        </Col>
                        <Col span={10}>
                          <Form.Item
                            name={[field.name, 'value']}
                            label={t('admin.pages.order_post_processors.fields.value', 'Value')}
                            rules={[{ required: true }]}
                          >
                            <Input placeholder="Value or Product ID" />
                          </Form.Item>
                        </Col>
                      </Row>

                      <Divider dashed orientation="left" style={{ margin: '10px 0' }}>
                        {t('admin.pages.order_post_processors.sections.actions', 'Actions')}
                      </Divider>

                      {/* Nested Actions List */}
                      <Form.List name={[field.name, 'order_post_processor_actions_attributes']}>
                        {(actionFields, { add: addAction, remove: removeAction }) => (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {actionFields.map((actionField) => (
                              <div key={actionField.key} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                                <Form.Item name={[actionField.name, 'id']} hidden><Input /></Form.Item>
                                <Form.Item name={[actionField.name, '_destroy']} hidden><Input /></Form.Item>

                                <Form.Item
                                  name={[actionField.name, 'action_type']}
                                  style={{ width: 200, marginBottom: 0 }}
                                  rules={[{ required: true }]}
                                >
                                  <Select placeholder={t('common.placeholder.select', 'Select')} options={[
                                    { label: t('common.order_rules.action.add_product', 'Add Gift'), value: 'add_product' },
                                  ]} />
                                </Form.Item>

                                {/* Parameters - Currently simplified as JSON string or specific fields.
                                    For 'add_product', we need product_id and qty.
                                    Ideally, this should be a dynamic sub-form based on action_type.
                                    For now, let's use a crude 'parameters' JSON input or specific fields assuming add_product is primary.
                                */}
                                {/* Since 'action_config' is JSONB in backend, we need to handle it carefully.
                                    Let's assume we extract 'product_id' and 'quantity' for UI and pack it on submit?
                                    Or just use direct fields if the backend accepts nested attributes mapped to action_config?
                                    Rails accepts_nested_attributes doesn't automatically map fields to a JSON column.
                                    We need to handle this in the onFinish or use a custom component.

                                    Simplest approach for MVP:
                                    Input fields for 'product_id' and 'quantity' and merge them into 'action_config' in onFinish.
                                    BUT, Antd Form binding to 'action_config.product_id' works if initialValue is set correctly.
                                */}
                                <Form.Item
                                  name={[actionField.name, 'action_config', 'product_id']}
                                  style={{ width: 200, marginBottom: 0 }}
                                  rules={[{ required: true, message: 'Product ID req' }]}
                                >
                                  <Input placeholder="Product ID (Gift)" />
                                </Form.Item>
                                <Form.Item
                                  name={[actionField.name, 'action_config', 'quantity']}
                                  style={{ width: 100, marginBottom: 0 }}
                                  initialValue={1}
                                >
                                  <InputNumber min={1} placeholder="Qty" />
                                </Form.Item>

                                <MinusCircleOutlined onClick={() => removeAction(actionField.name)} style={{ marginTop: 8 }} />
                              </div>
                            ))}
                            <Button type="dashed" onClick={() => addAction()} block icon={<PlusOutlined />}>
                              {t('admin.pages.order_post_processors.actions.add_action', 'Add Action')}
                            </Button>
                          </div>
                        )}
                      </Form.List>
                    </Card>
                  ))}
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                    {t('admin.pages.order_post_processors.actions.add_rule', 'Add Rule')}
                  </Button>
                </div>
              )}
            </Form.List>
          </Card>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                disabled={savingAndAdding}
              >
                {isNew ? t('common.button.create', 'Create') : t('common.save', 'Save')}
              </Button>
              {isNew && (
                <Button
                  loading={savingAndAdding}
                  disabled={loading}
                  onClick={handleSaveAndAddAnother}
                >
                  {t('common.button.save_and_add_another')}
                </Button>
              )}
              <Button onClick={() => navigate('/order_post_processors')}>
                {t('common.cancel', 'Cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Spin>
    </div>
  );
}
